<template>
  <div data-ui-section="comments">
    <div
      id="container--comments"
      class="container container--comments px-0"
    >
      <div
        id="comments"
        class="comments mt-0"
      >
        <post-reply-box
          ref="commentReplyBox"
          class="d-none d-md-block"
          :user="user"
          persistent
          :video-id="video.id"
          :before-open="checkForLogin"
          @success="updateParent"
        />

        <p class="comment__new__contactus hidden">
          {{ $t('video.action.conversations.having_trouble') }}
          <a
            href="#"
            data-toggle="modal"
            data-target="#modal-support"
          >{{ $t('video.action.conversations.contact_us') }}</a>
        </p>

        <hr>

        <div class="comment-content">
          <div
            v-for="comment in data.comments"
            :key="comment.id"
            class="comment rounded"
          >
            <comment-post
              :comment="comment"
              :user="user"
              :video-id="video.id"
              @success="updateParent"
            />
          </div>
        </div>

        <button
          v-if="hasMorePages"
          ref="seeMoreSpinner"
          type="button"
          class="vs-btn btn btn--outline--dark rounded w-100"
          @click="loadComments"
        >
          <span>{{ $t('video.action.conversations.see_more') }}</span>
          <i
            class="status"
            :class="isLoading ? 'loading' : ''"
          />
        </button>

        <post-reply-box
          ref="commentReplyBox"
          class="d-block d-md-none"
          :user="user"
          persistent
          :video-id="video.id"
          :before-open="checkForLogin"
          @success="updateParent"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/camelcase */

import Vue, { PropType } from 'vue';
import { Model } from '@viddsee/server-api-client';

import webcall from '@/scripts/webcall';
import loginController from '@/scripts/libs/login/controller';
import viddsee from '@/scripts/viddsee';

import WaypointHandler from '@/scripts/Components/libs/waypoint-handler';

import Post from './Post.vue';
import PostReplyBox from './PostReplyBox.vue';

interface ComponentData {
  user: null | any;
  data: {
    admin: boolean;
    comments: any[];
    current_page: number;
    total_pages: number;
    total_comments: number;
  };

  // UI Related
  waypoint: null | WaypointHandler;
  isLoading: boolean;
}

export default Vue.extend({
  components: {
    'comment-post': Post,
    'post-reply-box': PostReplyBox,
  },
  props: {
    video: {
      type: Object as PropType<Model.Video.Info>,
      required: true,
    },
  },
  data(): ComponentData {
    return {
      user: null,
      data: {
        admin: false,
        comments: [],
        current_page: -1,
        total_pages: 0,
        total_comments: 0,
      },

      // UI Related
      waypoint: null,
      isLoading: false,
    };
  },
  computed: {
    hasMorePages(): boolean {
      return this.data.current_page < this.data.total_pages;
    },
  },
  mounted() {
    loginController.getUser()
      .then(user => { this.user = user; })
      .catch((error) => {
        if (error.message === 'Unauthorized') return;
        throw error;
      })
    ;

    this.waypoint = new WaypointHandler({
      element: this.$refs.seeMoreSpinner as HTMLElement,
      handler: this.loadComments,
      offset: 'bottom-in-view',
      destoyOnTrigger: true,
    }, () => this.hasMorePages);
  },
  methods: {
    updateParent(response: any): void {
      const parentId = response.parent_id;

      response.isNew = true;

      if (parentId) { // has parent
        const commentsIndex = this.data.comments.findIndex(comment => comment.id === parentId);
        const replies = (this.data.comments[commentsIndex].replies || []);
        replies.push(response);
        this.$set(this.data.comments[commentsIndex], 'replies', replies);
      } else { // is new comment
        this.data.comments.unshift(response);
        this.$set(this.data, 'comments', this.data.comments);
      }
    },

    checkForLogin(): Promise<void> {
      if (!this.user) {
        window.ga('send', 'event', 'onboarding', 'comment-login');
        viddsee.ui.modal.register.open({
          callback: 'new_comment',
          loginReason: this.$t('javascript.login-reason.common-community-interaction'),
        });
        return Promise.reject(new Error('User not login'));
      }
      return Promise.resolve();
    },

    loadComments(/* _sortBy: string */): void {
      this.isLoading = true;

      webcall.video.getComments({
        video_id: this.video.id,
        current_page: this.data.current_page + 1,
      })
        .then((data) => {
          if ((this.$el as HTMLElement).offsetWidth === 0 && (this.$el as HTMLElement).offsetHeight === 0) {
            // Conversation is currently hidden, stop updating the page
            // The is necessary so that truncate will not take place when conversation is hidden
            this.isLoading = false;
            return;
          }

          if (this.user && this.user.adminAccess) {
            data.admin = this.user.adminAccess;
          }

          // Concatenate existing and new comment together
          const comments = [
            ...this.data.comments,
            ...data.comments,
          ];

          // Update UI State
          this.data = {
            ...data,
            comments,
          };

          this.waypoint && this.waypoint.update();
          this.isLoading = false;
        })
        .catch((error) => {
          this.isLoading = false;
          throw error;
        });
    },
  },
});
</script>

<style>

</style>
